import type { LabelDto } from '@tms/shared';
import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  LabelChip,
  Menu,
  MenuItem,
  MenuLabel,
  SkeletonRows,
} from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { LabelFormDialog } from './LabelFormDialog';
import { useDeleteLabel, useLabels } from './api';

/** Label administration for a project: add, rename, recolour, remove. */
export const LabelsCard = ({ projectCode }: { projectCode: string }) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { errorText } = useErrorText();
  const { data: labels, isPending } = useLabels(projectCode);
  const remove = useDeleteLabel(projectCode);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LabelDto | undefined>(undefined);
  const [deleting, setDeleting] = useState<LabelDto | undefined>(undefined);

  const canManage = can('label:manage');

  const openCreate = (): void => {
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (label: LabelDto): void => {
    setEditing(label);
    setFormOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(t('labels.deleted', { name: deleting.name }));
      setDeleting(undefined);
    } catch (error) {
      toast.error(errorText(error, 'labels.deleteFailed'));
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={t('labels.title')}
          description={t('labels.hint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                <Plus className="size-3.5" />
                {t('labels.new')}
              </Button>
            ) : null
          }
        />
        <CardBody>
          {isPending ? (
            <SkeletonRows rows={2} className="p-0" />
          ) : (labels ?? []).length === 0 ? (
            <p className="flex items-center gap-2 text-[13px] text-ink-subtle">
              <Tag className="size-3.5" />
              {t('labels.empty')}
            </p>
          ) : (
            /* Chips that wrap, not a row per label: a project with thirty of
               them takes three lines instead of thirty. Each chip opens what
               can be done with it. */
            <ul className="flex flex-wrap gap-1.5">
              {(labels ?? []).map((label) => (
                <li key={label.id}>
                  {canManage ? (
                    <Menu
                      align="start"
                      trigger={
                        <button
                          type="button"
                          aria-label={t('labels.actionsFor', { name: label.name })}
                          className="rounded-md outline-none ring-offset-1 ring-offset-surface transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-accent"
                        >
                          <LabelChip label={label} />
                        </button>
                      }
                    >
                      <MenuLabel>
                        {label.caseCount + label.checklistCount === 0
                          ? t('labels.notUsed')
                          : t('labels.usage', {
                              cases: label.caseCount,
                              checklists: label.checklistCount,
                            })}
                      </MenuLabel>
                      <MenuItem onSelect={() => openEdit(label)}>
                        <Pencil className="size-3.5" />
                        {t('common.edit')}
                      </MenuItem>
                      <MenuItem destructive onSelect={() => setDeleting(label)}>
                        <Trash2 className="size-3.5" />
                        {t('common.delete')}
                      </MenuItem>
                    </Menu>
                  ) : (
                    <LabelChip label={label} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <LabelFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        projectCode={projectCode}
        label={editing}
      />

      <ConfirmDialog
        open={deleting !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleting(undefined);
        }}
        title={t('labels.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('labels.deleteHint', {
          cases: deleting?.caseCount ?? 0,
          checklists: deleting?.checklistCount ?? 0,
        })}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
